import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, Plus, X, ArrowLeft, LayoutGrid, Tag, Megaphone, TrendingUp, PauseCircle, Eye, EyeOff } from 'lucide-react';

// Sprint 75 (1-2/N) — Desarrollos Inmobiliarios: cada desarrollo tiene su
// propio equipo de ventas + IA (S75 1/N) y su propio mapa de disponibilidad
// de unidades (S75 2/N), estilo Dealider: torres → pisos → unidades,
// coloreadas por estado de venta real.
const SALE_TYPES = [
  { key: 'apartment', label: 'Departamentos' },
  { key: 'house', label: 'Casas' },
  { key: 'land', label: 'Terrenos' },
  { key: 'macro_lot', label: 'Macrolotes' },
];

const STATUS_STYLES = {
  available: { label: 'Disponible', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700' },
  reserved: { label: 'Apartado', bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700' },
  sold: { label: 'Vendido', bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700' },
  blocked: { label: 'Bloqueado', bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-500' },
};

export default function DevelopmentManager() {
  const { user } = useAuth();
  // S137 (1/N) — suspender congela la venta de todo un desarrollo, así que el
  // botón solo existe para el Dueño. El servidor lo exige igual; esconderlo
  // aquí es para no enseñar una puerta que no abre, no para cerrarla.
  const esDueno = user?.tenant_role === 'tenant_owner';
  const [mostrarSuspendidos, setMostrarSuspendidos] = useState(false);
  const [suspendiendo, setSuspendiendo] = useState(null); // desarrollo elegido
  const [motivoSuspension, setMotivoSuspension] = useState('');
  const [guardandoSuspension, setGuardandoSuspension] = useState(false);
  const [developments, setDevelopments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newDev, setNewDev] = useState({ name: '', sale_type: 'apartment', city: '', reservation_hold_hours: 72 });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null); // development being viewed in grid mode
  const [grid, setGrid] = useState(null);
  const [gridLoading, setGridLoading] = useState(false);

  // Sprint 76 (5/N) — el flujo real de venta (apartar/confirmar/cancelar
  // unidad) y los avisos de avance de obra tenían API completa desde S75
  // (4/N, 8/N, 10/N) pero cero UI: solo se podía configurar el desarrollo,
  // nunca vender nada desde aquí.
  const [reservations, setReservations] = useState([]);
  const [leads, setLeads] = useState([]);
  const [reservingUnit, setReservingUnit] = useState(null); // catalog_item_id being reserved
  const [reserveForm, setReserveForm] = useState({ crm_lead_id: '', amount: '' });
  const [reserving, setReserving] = useState(false);
  const [actingReservationId, setActingReservationId] = useState(null);

  const [progressUpdates, setProgressUpdates] = useState([]);
  const [showProgressForm, setShowProgressForm] = useState(false);
  const [progressForm, setProgressForm] = useState({ title: '', description: '', percent_complete: '' });
  const [savingProgress, setSavingProgress] = useState(false);

  const [showLaunchForm, setShowLaunchForm] = useState(false);
  const [launchPhase, setLaunchPhase] = useState('');
  const [announcingLaunch, setAnnouncingLaunch] = useState(false);

  // Fase 2 — alta de unidad desde el desarrollo + filtro por tipo/estado
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [unitForm, setUnitForm] = useState({
    item_type: 'property_apartment', name: '', unit_number: '', tower: '', floor: '',
    price_min: '', bedrooms: '', bathrooms: '', construction_m2: '', land_m2: '1',
  });
  const [savingUnit, setSavingUnit] = useState(false);
  const [gridTypeFilter, setGridTypeFilter] = useState('all');
  const [gridStatusFilter, setGridStatusFilter] = useState('all');


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/catalog/developments',
        { params: { incluir_suspendidos: mostrarSuspendidos } });
      setDevelopments(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [mostrarSuspendidos]);

  // S137 (1/N) — el motivo se valida también aquí para que el aviso salga al
  // instante, pero el que manda es el servidor: la pantalla no es la guardia.
  const handleSuspender = async () => {
    const texto = motivoSuspension.trim();
    if (texto.length < 10) {
      toast.error('Escribe el motivo (al menos 10 caracteres). Queda guardado para que cualquiera entienda después por qué se suspendió.');
      return;
    }
    setGuardandoSuspension(true);
    try {
      await api.post(`/tenant/catalog/developments/${suspendiendo.id}/suspender`, { motivo: texto });
      toast.success(`«${suspendiendo.name}» quedó suspendido. Su información se conserva completa.`);
      setSuspendiendo(null); setMotivoSuspension(''); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo suspender'); }
    finally { setGuardandoSuspension(false); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/tenant/crm/leads').then(r => setLeads(r.data?.items || r.data || [])).catch(() => setLeads([]));
  }, []);

  const loadReservations = async (developmentId) => {
    try {
      const { data } = await api.get('/tenant/erp/reservations', { params: { development_id: developmentId, status: 'active' } });
      setReservations(data || []);
    } catch { setReservations([]); }
  };

  const loadProgressUpdates = async (developmentId) => {
    try {
      const { data } = await api.get(`/tenant/erp/developments/${developmentId}/progress-updates`);
      setProgressUpdates(data || []);
    } catch { setProgressUpdates([]); }
  };

  const handleCreate = async () => {
    if (!newDev.name.trim()) { toast.error('Ponle un nombre al desarrollo'); return; }
    setSaving(true);
    try {
      await api.post('/tenant/catalog/developments', newDev);
      toast.success('Desarrollo creado — ya tiene su propio equipo de ventas e IA');
      setNewDev({ name: '', sale_type: 'apartment', city: '', reservation_hold_hours: 72 });
      setShowNew(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo crear el desarrollo'); }
    finally { setSaving(false); }
  };

  const handleCreateUnit = async () => {
    if (!selected) return;
    if (!unitForm.name.trim() && !unitForm.unit_number.trim()) {
      toast.error('Ponle nombre o número a la unidad'); return;
    }
    setSavingUnit(true);
    try {
      const land = unitForm.land_m2 || '1';
      const body = {
        item_type: unitForm.item_type,
        name: unitForm.name.trim() || `Unidad ${unitForm.unit_number || ''}`.trim(),
        description: '',
        price_min: unitForm.price_min ? Number(unitForm.price_min) : null,
        development_id: selected.id,
        tower: unitForm.tower || null,
        floor: unitForm.floor !== '' ? Number(unitForm.floor) : null,
        unit_number: unitForm.unit_number || null,
        sales_status: 'available',
        land_m2: Number(land) || 1,
        construction_m2: unitForm.construction_m2 ? Number(unitForm.construction_m2) : null,
        bedrooms: unitForm.bedrooms !== '' ? Number(unitForm.bedrooms) : null,
        bathrooms: unitForm.bathrooms !== '' ? Number(unitForm.bathrooms) : null,
      };
      await api.post('/tenant/catalog/items', body);
      toast.success('Unidad creada en este desarrollo');
      setShowUnitForm(false);
      setUnitForm({
        item_type: unitForm.item_type, name: '', unit_number: '', tower: unitForm.tower, floor: '',
        price_min: '', bedrooms: '', bathrooms: '', construction_m2: '', land_m2: land,
      });
      openGrid(selected);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo crear la unidad');
    } finally { setSavingUnit(false); }
  };

  const openGrid = async (dev) => {
    setSelected(dev);
    setGridLoading(true);
    setGrid(null);
    setReservingUnit(null);
    setShowUnitForm(false);
    setGridTypeFilter('all');
    setGridStatusFilter('all');
    const sale = dev.sale_type || 'apartment';
    const typeMap = { apartment: 'property_apartment', house: 'property_house', land: 'property_land', macro_lot: 'property_macro_lot' };
    setUnitForm(f => ({ ...f, item_type: typeMap[sale] || 'property_apartment' }));
    try {
      const { data } = await api.get(`/tenant/catalog/developments/${dev.id}/grid`);
      setGrid(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar el mapa de disponibilidad'); }
    finally { setGridLoading(false); }
    loadReservations(dev.id);
    loadProgressUpdates(dev.id);
  };

  const openReserveForm = (unitId) => {
    setReservingUnit(unitId);
    setReserveForm({ crm_lead_id: '', amount: '' });
  };

  const handleReserve = async () => {
    if (!reserveForm.crm_lead_id) { toast.error('Elige el lead que aparta la unidad'); return; }
    setReserving(true);
    try {
      await api.post(`/tenant/erp/units/${reservingUnit}/reserve`, {
        crm_lead_id: reserveForm.crm_lead_id,
        amount: reserveForm.amount ? Number(reserveForm.amount) : undefined,
      });
      toast.success(`Unidad apartada — se libera automáticamente si nadie la confirma en ${selected.reservation_hold_hours}h`);
      setReservingUnit(null);
      openGrid(selected);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo apartar la unidad'); }
    finally { setReserving(false); }
  };

  const handleConfirmReservation = async (reservationId) => {
    setActingReservationId(reservationId);
    try {
      await api.post(`/tenant/erp/reservations/${reservationId}/confirm`);
      toast.success('Apartado confirmado — la unidad ya no expira, queda bloqueada hasta la venta');
      openGrid(selected);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo confirmar el apartado'); }
    finally { setActingReservationId(null); }
  };

  const handleCancelReservation = async (reservationId) => {
    setActingReservationId(reservationId);
    try {
      await api.post(`/tenant/erp/reservations/${reservationId}/cancel`);
      toast.success('Apartado cancelado — la unidad vuelve a estar disponible');
      openGrid(selected);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cancelar el apartado'); }
    finally { setActingReservationId(null); }
  };

  const handlePublishProgress = async () => {
    if (!progressForm.title.trim()) { toast.error('Ponle un título al avance'); return; }
    setSavingProgress(true);
    try {
      const { data } = await api.post(`/tenant/erp/developments/${selected.id}/progress-updates`, {
        title: progressForm.title, description: progressForm.description || undefined,
        percent_complete: progressForm.percent_complete ? Number(progressForm.percent_complete) : undefined,
      });
      toast.success(`Avance publicado — ${data.buyers_notified} comprador(es) notificado(s)`);
      setShowProgressForm(false);
      setProgressForm({ title: '', description: '', percent_complete: '' });
      loadProgressUpdates(selected.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo publicar el avance'); }
    finally { setSavingProgress(false); }
  };

  const handleAnnounceLaunch = async () => {
    if (!launchPhase.trim()) { toast.error('Ponle nombre a la fase/etapa que lanzas'); return; }
    setAnnouncingLaunch(true);
    try {
      const { data } = await api.post(`/tenant/erp/developments/${selected.id}/announce-launch`, { phase_name: launchPhase });
      toast.success(`Lanzamiento anunciado — ${data.vip_leads_notified} lead(s) VIP notificado(s)`);
      setShowLaunchForm(false);
      setLaunchPhase('');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo anunciar el lanzamiento'); }
    finally { setAnnouncingLaunch(false); }
  };

  if (selected) {
    return (
      <div className="space-y-4" data-testid="development-grid-view">
        <div className="flex items-center gap-2">
          <button onClick={() => { setSelected(null); setGrid(null); }} className="text-xs text-[#64748B] hover:text-[#0F172A] flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Volver a Desarrollos
          </button>
        </div>
        <div>
          <h2 className="text-sm font-bold flex items-center gap-2">
            <LayoutGrid className="w-4 h-4" /> {selected.name}
            <HelpTooltip text="Mapa de disponibilidad: torre → piso → unidad, coloreado por estado de venta real. Puedes dar de alta unidades aquí; también aparecen en Unidades (antes Catálogo)." />
          </h2>
          <p className="text-xs text-[#64748B] mt-0.5">
            {SALE_TYPES.find(s => s.key === selected.sale_type)?.label || 'Sin tipo de venta'} · ventana de apartado {selected.reservation_hold_hours}h
          </p>
        </div>

        {gridLoading ? (
          <p className="text-xs text-[#94A3B8] text-center py-10">Cargando...</p>
        ) : !grid || grid.total_units === 0 ? (
          <div className="bg-white border rounded-xl p-8 text-center">
            <Building2 className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
            <p className="text-sm text-[#64748B]">Este desarrollo todavía no tiene unidades. Agrégalas desde Catálogo eligiendo este desarrollo en el formulario.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="development-grid-legend">
              {Object.entries(STATUS_STYLES).map(([key, s]) => (
                <span key={key} className={`text-xs px-2 py-1 rounded-full border ${s.bg} ${s.border} ${s.text}`}>
                  {s.label} ({grid.by_status[key] || 0})
                </span>
              ))}
            </div>
            {grid.towers.map(t => (
              <div key={t.tower || 'sin-torre'} className="bg-white border rounded-xl p-4 space-y-3">
                {t.tower && <p className="text-sm font-bold text-[#0F172A]">{t.tower}</p>}
                {t.floors.map(f => (
                  <div key={f.floor ?? 'sin-piso'} className="flex items-center gap-2 flex-wrap">
                    {f.floor !== null && <span className="text-xs text-[#94A3B8] w-16 flex-shrink-0">Piso {f.floor}</span>}
                    {f.units.filter(u => {
                      if (gridStatusFilter !== 'all' && u.sales_status !== gridStatusFilter) return false;
                      if (gridTypeFilter !== 'all' && u.item_type && u.item_type !== gridTypeFilter) return false;
                      return true;
                    }).map(u => {
                      const style = STATUS_STYLES[u.sales_status] || STATUS_STYLES.blocked;
                      const isAvailable = u.sales_status === 'available';
                      return (
                        <button key={u.id} onClick={() => isAvailable && openReserveForm(u.id)} disabled={!isAvailable}
                          className={`min-w-[84px] px-2 py-1.5 rounded-lg border text-center ${style.bg} ${style.border} ${isAvailable ? 'hover:ring-2 hover:ring-[#0F172A] cursor-pointer' : 'cursor-default'}`}
                          data-testid={`grid-unit-${u.id}`} title={isAvailable ? `${u.name} — clic para apartar` : u.name}>
                          <p className={`text-xs font-semibold ${style.text}`}>{u.unit_number || u.name}</p>
                          {u.price_min != null && <p className="text-[10px] text-[#64748B]">${Number(u.price_min).toLocaleString('es-MX')}</p>}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </>
        )}

        
        {showUnitForm && selected && (
          <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="dev-unit-form">
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-semibold">Nueva unidad en {selected.name}</h4>
              <button type="button" onClick={() => setShowUnitForm(false)}><X className="w-4 h-4 text-[#94A3B8]" /></button>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-[#64748B]">Tipo</label>
                <select value={unitForm.item_type} onChange={e => setUnitForm(f => ({ ...f, item_type: e.target.value }))}
                  className="w-full h-9 text-sm border rounded-lg px-2" data-testid="dev-unit-type">
                  <option value="property_apartment">Departamento</option>
                  <option value="property_house">Casa</option>
                  <option value="property_land">Terreno</option>
                  <option value="property_commercial">Local comercial</option>
                  <option value="property_industrial_lot">Lote industrial</option>
                  <option value="property_residential_lot">Lote residencial</option>
                  <option value="property_macro_lot">Macrolote</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Número / depto</label>
                <Input value={unitForm.unit_number} onChange={e => setUnitForm(f => ({ ...f, unit_number: e.target.value }))}
                  placeholder="A-101" data-testid="dev-unit-number" />
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Nombre (opcional)</label>
                <Input value={unitForm.name} onChange={e => setUnitForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Depto 101 Torre A" data-testid="dev-unit-name" />
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Torre</label>
                <Input value={unitForm.tower} onChange={e => setUnitForm(f => ({ ...f, tower: e.target.value }))}
                  placeholder="Torre A" data-testid="dev-unit-tower" />
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Piso</label>
                <Input type="number" value={unitForm.floor} onChange={e => setUnitForm(f => ({ ...f, floor: e.target.value }))}
                  data-testid="dev-unit-floor" />
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Precio</label>
                <Input type="number" value={unitForm.price_min} onChange={e => setUnitForm(f => ({ ...f, price_min: e.target.value }))}
                  data-testid="dev-unit-price" />
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Recámaras</label>
                <Input type="number" value={unitForm.bedrooms} onChange={e => setUnitForm(f => ({ ...f, bedrooms: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Baños</label>
                <Input type="number" value={unitForm.bathrooms} onChange={e => setUnitForm(f => ({ ...f, bathrooms: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-[#64748B]">m² construcción</label>
                <Input type="number" value={unitForm.construction_m2} onChange={e => setUnitForm(f => ({ ...f, construction_m2: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreateUnit} disabled={savingUnit} data-testid="dev-unit-save">
                {savingUnit ? 'Guardando...' : 'Guardar unidad'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowUnitForm(false)}>Cancelar</Button>
            </div>
          </div>
        )}

{reservingUnit && (
          <div className="bg-white border rounded-xl p-4 space-y-2" data-testid="reserve-unit-form">
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-semibold flex items-center gap-1.5"><Tag className="w-4 h-4" /> Apartar unidad</h4>
              <button onClick={() => setReservingUnit(null)}><X className="w-4 h-4 text-[#94A3B8]" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-[#64748B]">Lead</label>
                <Select value={reserveForm.crm_lead_id} onValueChange={v => setReserveForm({ ...reserveForm, crm_lead_id: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="reserve-unit-lead"><SelectValue placeholder="Elige..." /></SelectTrigger>
                  <SelectContent>{leads.map(l => <SelectItem key={l.id} value={l.id}>{l.display_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Monto del apartado (opcional)</label>
                <Input type="number" placeholder="10000" value={reserveForm.amount} onChange={e => setReserveForm({ ...reserveForm, amount: e.target.value })} data-testid="reserve-unit-amount" />
              </div>
            </div>
            <p className="text-[11px] text-[#94A3B8]">Se libera automáticamente en {selected.reservation_hold_hours}h si nadie la confirma.</p>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setReservingUnit(null)}>Cancelar</Button>
              <Button size="sm" onClick={handleReserve} disabled={reserving} data-testid="reserve-unit-save">{reserving ? 'Apartando...' : 'Apartar unidad'}</Button>
            </div>
          </div>
        )}

        {reservations.length > 0 && (
          <div className="bg-white border rounded-xl divide-y" data-testid="active-reservations-list">
            <p className="text-xs font-bold text-[#334155] p-3 pb-0">Apartados activos</p>
            {reservations.map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 text-xs" data-testid={`reservation-${r.id}`}>
                <div>
                  <p className="font-medium text-[#0F172A]">{r.unit_name} · {r.lead_name || 'Sin lead'}</p>
                  <p className="text-[#64748B]">Expira {r.expires_at ? new Date(r.expires_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleConfirmReservation(r.id)} disabled={actingReservationId === r.id} className="text-emerald-600 hover:underline" data-testid={`reservation-confirm-${r.id}`}>Confirmar</button>
                  <button onClick={() => handleCancelReservation(r.id)} disabled={actingReservationId === r.id} className="text-red-500 hover:underline" data-testid={`reservation-cancel-${r.id}`}>Cancelar</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white border rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-[#334155] flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> Avance de obra</p>
            <Button size="sm" variant="outline" onClick={() => setShowProgressForm(!showProgressForm)} data-testid="new-progress-update-btn">
              <Plus className="w-4 h-4 mr-1" /> Publicar avance
            </Button>
          </div>
          {showProgressForm && (
            <div className="p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-2">
              <Input placeholder="Título (ej. Cimentación terminada)" value={progressForm.title} onChange={e => setProgressForm({ ...progressForm, title: e.target.value })} className="h-8 text-xs" data-testid="progress-update-title" />
              <Input placeholder="Descripción (opcional)" value={progressForm.description} onChange={e => setProgressForm({ ...progressForm, description: e.target.value })} className="h-8 text-xs" />
              <Input type="number" min="0" max="100" placeholder="% de avance (opcional)" value={progressForm.percent_complete} onChange={e => setProgressForm({ ...progressForm, percent_complete: e.target.value })} className="h-8 text-xs w-40" />
              <p className="text-[11px] text-[#94A3B8]">Se notifica solo a compradores reales (con plan de pagos ligado a este desarrollo), no a cualquier lead interesado.</p>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowProgressForm(false)}>Cancelar</Button>
                <Button size="sm" className="h-7 text-xs" onClick={handlePublishProgress} disabled={savingProgress} data-testid="progress-update-save">{savingProgress ? 'Publicando...' : 'Publicar'}</Button>
              </div>
            </div>
          )}
          {progressUpdates.length === 0 ? (
            <p className="text-[11px] text-[#94A3B8]">Sin avances publicados todavía.</p>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="progress-updates-list">
              {progressUpdates.map(u => (
                <div key={u.id} className="text-xs bg-[#F8FAFC] rounded-lg px-2.5 py-1.5">
                  <p className="font-medium text-[#334155]">{u.title}{u.percent_complete != null ? ` — ${u.percent_complete}%` : ''}</p>
                  {u.description && <p className="text-[#64748B] mt-0.5">{u.description}</p>}
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">{u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX') : ''}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-[#334155] flex items-center gap-1.5"><Megaphone className="w-4 h-4" /> Anunciar lanzamiento de fase</p>
            <Button size="sm" variant="outline" onClick={() => setShowLaunchForm(!showLaunchForm)} data-testid="announce-launch-btn">Anunciar</Button>
          </div>
          {showLaunchForm && (
            <div className="p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-2">
              <Input placeholder="Nombre de la fase/etapa (ej. Preventa Torre Sur)" value={launchPhase} onChange={e => setLaunchPhase(e.target.value)} className="h-8 text-xs" data-testid="launch-phase-name" />
              <p className="text-[11px] text-[#94A3B8]">Cada lead VIP interesado en este desarrollo recibe su propia alerta.</p>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowLaunchForm(false)}>Cancelar</Button>
                <Button size="sm" className="h-7 text-xs" onClick={handleAnnounceLaunch} disabled={announcingLaunch} data-testid="announce-launch-save">{announcingLaunch ? 'Anunciando...' : 'Anunciar lanzamiento'}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="development-manager">
      <div>
        <h2 className="text-sm font-bold flex items-center gap-2">
          <Building2 className="w-4 h-4" /> Desarrollos
          <HelpTooltip text="Cada desarrollo tiene su propio equipo de ventas y su propia configuración de IA, aunque compartan la misma marca. También tiene su propio mapa de disponibilidad de unidades." />
        </h2>
        <p className="text-xs text-[#64748B] mt-0.5">Un desarrollo = un tipo de venta. Si vendes mixto (casas y departamentos), crea un desarrollo por cada tipo.</p>
      </div>

      <SectionHelp
        module="real_estate_developments"
        title="Desarrollos"
        description="Cada desarrollo es su propia unidad de negocio: equipo de ventas propio, Habilidad de IA propia, y mapa de disponibilidad propio — todo bajo la misma marca del tenant."
        whatItDoesNot="No reemplaza el Catálogo — las unidades siguen siendo ítems de catálogo, aquí solo se agrupan y visualizan por desarrollo."
      />

      <div className="flex justify-end items-center gap-2">
        {/* S137 (1/N) — los suspendidos no se borran, se esconden. Este
            interruptor es la única forma de volverlos a ver. */}
        <Button size="sm" variant="outline" data-testid="toggle-suspendidos"
          onClick={() => setMostrarSuspendidos(v => !v)}>
          {mostrarSuspendidos
            ? <><EyeOff className="w-4 h-4 mr-1" /> Ocultar suspendidos</>
            : <><Eye className="w-4 h-4 mr-1" /> Mostrar suspendidos</>}
        </Button>
        <Button size="sm" onClick={() => setShowNew(!showNew)} data-testid="new-development-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo desarrollo
        </Button>
      </div>
      <p className="text-[11px] text-[#94A3B8] text-right -mt-1">
        Un desarrollo suspendido deja de venderse y de recibir leads, y nadie puede modificarlo.
        Toda su información —compradores, pagos, garantías— se conserva completa.
        Solo CavernicolApp puede reactivarlo.
      </p>

      {showNew && (
        <div className="bg-white border rounded-xl p-4 space-y-2">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-semibold">Nuevo desarrollo</h4>
            <button onClick={() => setShowNew(false)}><X className="w-4 h-4 text-[#94A3B8]" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-[#64748B]">Nombre</label>
              <Input placeholder="Torre Marina" value={newDev.name}
                onChange={e => setNewDev({ ...newDev, name: e.target.value })} data-testid="new-development-name" />
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Tipo de venta</label>
              <select value={newDev.sale_type} onChange={e => setNewDev({ ...newDev, sale_type: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="new-development-sale-type">
                {SALE_TYPES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Ciudad</label>
              <Input placeholder="Acapulco" value={newDev.city} onChange={e => setNewDev({ ...newDev, city: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Ventana de apartado (horas)</label>
              <Input type="number" min={1} value={newDev.reservation_hold_hours}
                onChange={e => setNewDev({ ...newDev, reservation_hold_hours: Math.max(1, +e.target.value) })} />
              <p className="text-[11px] text-[#94A3B8] mt-0.5">Si nadie completa el trámite en este tiempo, la unidad se libera automáticamente.</p>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="new-development-save">
              {saving ? 'Guardando...' : 'Crear desarrollo'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-[#94A3B8] text-center py-10">Cargando...</p>
      ) : developments.length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center">
          <Building2 className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Todavía no tienes desarrollos. Crea el primero arriba.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="developments-list">
          {developments.map(d => (
            // S137 (1/N) — la tarjeta dejó de ser un <button> para poder llevar
            // el botón de Suspender dentro. Un botón dentro de otro es HTML
            // inválido y el clic se vuelve impredecible.
            <div key={d.id}
              className={`border rounded-xl p-4 transition-colors ${d.suspendido
                ? 'bg-amber-50/60 border-amber-300'
                : 'bg-white hover:border-[#0F172A]'}`}
              data-testid={`development-card-${d.id}`}>
              <button onClick={() => openGrid(d)} className="text-left w-full"
                data-testid={`development-open-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-bold text-[#0F172A]">{d.name}</p>
                  {d.suspendido && (
                    <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900"
                      data-testid={`development-suspendido-${d.id}`}>SUSPENDIDO</span>
                  )}
                </div>
                <p className="text-xs text-[#64748B] mt-0.5">
                  {SALE_TYPES.find(s => s.key === d.sale_type)?.label || 'Sin tipo de venta'}{d.city ? ` · ${d.city}` : ''}
                </p>
                <p className="text-[11px] text-[#94A3B8] mt-1">Ver mapa de disponibilidad →</p>
              </button>

              {d.suspendido ? (
                <div className="mt-3 pt-3 border-t border-amber-200">
                  <p className="text-[11px] text-amber-900">
                    <strong>Motivo:</strong> {d.motivo_suspension || 'sin motivo registrado'}
                  </p>
                  {d.suspendido_el && (
                    <p className="text-[11px] text-amber-700 mt-0.5">
                      Suspendido el {new Date(d.suspendido_el).toLocaleDateString('es-MX')}
                    </p>
                  )}
                  <p className="text-[11px] text-[#64748B] mt-1">
                    Para volver a trabajarlo, pídele a CavernicolApp que lo reactive.
                  </p>
                </div>
              ) : esDueno && (
                <div className="mt-3 pt-3 border-t flex justify-end">
                  <Button size="sm" variant="outline"
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    onClick={() => { setSuspendiendo(d); setMotivoSuspension(''); }}
                    data-testid={`development-suspender-${d.id}`}>
                    <PauseCircle className="w-3.5 h-3.5 mr-1" /> Suspender
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* S137 (1/N) — el motivo es obligatorio y la ventana dice de antemano
          QUÉ va a pasar. Es una decisión que congela el trabajo de un equipo
          entero y que este negocio no puede deshacer solo. */}
      {suspendiendo && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          data-testid="suspender-modal">
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-3">
            <div className="flex justify-between items-start">
              <h4 className="text-sm font-bold text-[#0F172A]">
                Suspender «{suspendiendo.name}»
              </h4>
              <button onClick={() => setSuspendiendo(null)} aria-label="Cerrar">
                <X className="w-4 h-4 text-[#94A3B8]" />
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-900">Al suspenderlo:</p>
              <ul className="text-[11px] text-amber-900 space-y-0.5 list-disc list-inside">
                <li>Desaparece de la lista y deja de venderse.</li>
                <li>Los leads nuevos que le lleguen caen <strong>sin campaña</strong>, para que los reasignes.</li>
                <li>Nadie podrá modificarlo, ni a él ni a sus unidades.</li>
                <li><strong>Todo lo ya ocurrido se conserva</strong>: compradores, pagos, garantías e historial.</li>
                <li>Solo CavernicolApp puede reactivarlo.</li>
              </ul>
            </div>

            <div>
              <label className="text-xs text-[#64748B]">
                ¿Por qué lo suspendes? <span className="text-red-600">*</span>
              </label>
              <textarea rows={3} value={motivoSuspension}
                onChange={e => setMotivoSuspension(e.target.value)}
                placeholder="Ejemplo: se agotaron las unidades disponibles y no habrá nueva etapa este año."
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                data-testid="suspender-motivo" />
              <p className="text-[11px] text-[#94A3B8] mt-0.5">
                Queda guardado. Es lo único que le explicará a alguien, dentro de seis meses,
                por qué este desarrollo está congelado.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setSuspendiendo(null)}>Cancelar</Button>
              <Button size="sm" onClick={handleSuspender} disabled={guardandoSuspension}
                className="bg-amber-600 hover:bg-amber-700" data-testid="suspender-confirmar">
                {guardandoSuspension ? 'Suspendiendo...' : 'Suspender desarrollo'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
